import { useMemo, useCallback, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { recoverRequestsApi, type RecoveryAssignment } from '../api'
import { useObject } from '@/features/objects/hooks'
import { RunbookRecoveryTable } from './RunbookRecoveryTable'
import { RecoveryTrackingHeader } from './RecoveryTrackingHeader'
import { AddRecoveryUpdateDialog } from './AddRecoveryUpdateDialog'
import { MobilisationTab } from './mobilisation'
import { useMobilisationStatus } from '../hooks'
import { computeForecastTimesMap } from './columns-runbook-recovery'
import { truncateToMinuteFloor } from '@/lib/dateUtils'
import { formatSecondsToDHM } from '@/features/objects/utils/formatTime'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, LayoutDashboard, Radio, ListOrdered, Package } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import type { RecoveryRunbookTask } from '@/types/entities'
import type { RecoveryHeaderUpdate } from './RecoveryTrackingHeader'
import { calculateRunbookTimes } from '@/features/objects/utils/calculateRunbookTimes'
import { useRunbookAiSession } from '../hooks/useRunbookAiSession'
import { useAiChatPanel } from '@/contexts/AiChatPanelContext'

export interface RecoveryManagementScreensProps {
    requestID: number | string
    objectID?: string
    objectDescription?: string | null
    forecastStartTime?: string | Date | null
    title?: string
    showBackButton?: boolean
    onBack?: () => void
    readOnly?: boolean
    /** When true, show Dashboard / Mobilisation / Request IDs / Objects tabs (for recovery set context). Runbook Update does not use these. */
    showRecoverySetTabs?: boolean
}

export function RecoveryManagementScreens({
    requestID,
    objectID: objectIDProp = '',
    objectDescription = null,
    forecastStartTime: forecastStartTimeProp = null,
    title,
    showBackButton = false,
    onBack,
    readOnly = false,
    showRecoverySetTabs = false,
}: RecoveryManagementScreensProps) {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const { toast } = useToast()
    const [pauseRefetch, setPauseRefetch] = useState(false)

    const forecastStartTime = useMemo(() => {
        if (!forecastStartTimeProp) return new Date()
        if (forecastStartTimeProp instanceof Date) return forecastStartTimeProp
        if (typeof forecastStartTimeProp === 'string') {
            const d = new Date(forecastStartTimeProp)
            return isNaN(d.getTime()) ? new Date() : d
        }
        return new Date()
    }, [forecastStartTimeProp])

    const requestIDKey = typeof requestID === 'number' ? requestID : String(requestID).trim()
    const objectID = objectIDProp || String(requestID)
    const { data: object } = useObject(objectIDProp || null)
    const { data: recoveryRunbookResponse, isLoading } = useQuery({
        queryKey: ['recovery-runbook', requestID],
        queryFn: async () => {
            const response = await recoverRequestsApi.getRecoveryRunbook(requestID)
            return response.data?.data ?? null
        },
        enabled: typeof requestID === 'number' ? requestID > 0 : requestIDKey.length > 0,
        refetchOnMount: 'always',
        refetchInterval: pauseRefetch ? false : 60000,
        placeholderData: keepPreviousData,
    })

    const { data: recoveryAssignmentsData } = useQuery({
        queryKey: ['recovery-assignments', requestID],
        queryFn: async () => {
            try {
                const response = await recoverRequestsApi.getRecoveryAssignments(requestID)
                const payload = response.data?.data
                if (!payload) return { assignments: [], updateTimeRemainingMinutes: null }
                if (Array.isArray(payload)) return { assignments: payload, updateTimeRemainingMinutes: null }
                return { assignments: payload.assignments ?? [], updateTimeRemainingMinutes: payload.updateTimeRemainingMinutes ?? null }
            } catch (err: unknown) {
                const status = (err as { response?: { status?: number } })?.response?.status
                if (status === 404) return { assignments: [], updateTimeRemainingMinutes: null }
                throw err
            }
        },
        enabled: typeof requestID === 'number' ? requestID > 0 : requestIDKey.length > 0,
        retry: (_, error) => {
            const status = (error as { response?: { status?: number } })?.response?.status
            return status !== 404
        },
    })

    const requestIDNumForHooks = typeof requestID === 'number' ? requestID : null
    const { data: mobilisationStatus } = useMobilisationStatus(requestIDNumForHooks)
    const mobilisationCompleted = mobilisationStatus?.completedSteps ?? 0
    const mobilisationTotal = 5

    const updateMutation = useMutation({
        mutationFn: async (tasks: RecoveryRunbookTask[]) => {
            const payload = tasks
                .filter((t) => t.id != null)
                .map((task) => {
                    const p: Record<string, unknown> = { id: task.id! }
                    if (task.taskStatus !== undefined) p.taskStatus = task.taskStatus
                    if (task.taskOwner !== undefined) p.taskOwner = task.taskOwner
                    if (task.actualStart != null && task.actualStart !== '') p.actualStart = task.actualStart
                    if (task.actualFinish != null && task.actualFinish !== '') p.actualFinish = task.actualFinish
                    if (task.actualDuration != null) p.actualDuration = task.actualDuration
                    if (task.forecastDuration != null) p.forecastDuration = task.forecastDuration
                    if (task.duration != null) p.duration = task.duration
                    if (task.forecastTaskOffset != null) p.forecastTaskOffset = task.forecastTaskOffset
                    return p
                })
                .filter((t) => Object.keys(t).length > 1)
            if (payload.length === 0) return
            await recoverRequestsApi.updateRecoveryRunbook(requestID, payload)
        },
        onSuccess: async () => {
            await queryClient.refetchQueries({ queryKey: ['recovery-runbook', requestID] })
            toast({ title: 'Saved', description: 'Recovery runbook updated.' })
        },
        onError: (error: any) => {
            toast({
                title: 'Failed to save',
                description: error?.response?.data?.message || error.message,
                variant: 'destructive',
            })
        },
    })

    const runbookTasksRaw = useMemo(() => {
        const data = recoveryRunbookResponse
        if (!data) return []
        if (Array.isArray(data)) return data
        return Array.isArray((data as { tasks?: unknown[] }).tasks) ? (data as { tasks: unknown[] }).tasks : []
    }, [recoveryRunbookResponse])

    const runbookForecastStart = useMemo(() => {
        const data = recoveryRunbookResponse
        if (!data || Array.isArray(data)) return null
        const d = data as { forecastStartTime?: string | null }
        return d.forecastStartTime ? new Date(d.forecastStartTime) : null
    }, [recoveryRunbookResponse])

    const requestObjectCount = useMemo(() => {
        const data = recoveryRunbookResponse
        if (!data || Array.isArray(data)) return 0
        const d = data as { requestObjectCount?: number | null }
        return d.requestObjectCount != null ? Number(d.requestObjectCount) : 0
    }, [recoveryRunbookResponse])

    const tasks: RecoveryRunbookTask[] = useMemo(() => {
        if (!runbookTasksRaw.length) return []
        const raw = runbookTasksRaw.map((t: any) => ({
            id: t.id ?? null,
            taskNo: t.taskNo ?? null,
            waitGo: t.waitGo ?? null,
            task: t.task || '',
            commands: t.commands || '',
            duration: t.duration ?? null,
            taskOffset: t.taskOffset ?? null,
            startTime: null,
            finishTime: null,
            dependencies: t.dependencies || '',
            taskStatus: t.taskStatus ?? null,
            taskOwner: t.taskOwner ?? null,
            plannedStart: t.plannedStart ?? null,
            plannedFinish: t.plannedFinish ?? null,
            actualStart: t.actualStart ?? null,
            actualFinish: t.actualFinish ?? null,
            actualDuration: t.actualDuration ?? null,
            forecastDuration: t.forecastDuration ?? null,
            forecastTaskOffset: t.forecastTaskOffset ?? null,
        }))
        return calculateRunbookTimes(raw) as RecoveryRunbookTask[]
    }, [runbookTasksRaw])

    const handleSave = useCallback(
        (tasksToSave: RecoveryRunbookTask[]) => updateMutation.mutate(tasksToSave),
        [updateMutation]
    )

    // AI assistant session linked to this runbook execution
    const { sessionId: aiSessionId } = useRunbookAiSession({
        requestID,
        objectID,
        objectDescription,
        tasks,
        enabled: !readOnly,
    })
    const aiChatPanel = useAiChatPanel()

    const handleTaskIssue = useCallback(
        (task: RecoveryRunbookTask) => {
            if (!aiSessionId) return
            const message = `Task ${task.taskNo} has an issue: "${task.task || 'N/A'}". What could be wrong and how do I fix it?`
            aiChatPanel.open(aiSessionId, message)
        },
        [aiSessionId, aiChatPanel]
    )

    const headerData = useMemo(() => {
        if (!tasks.length) return null
        const task1 = tasks.find((t) => Number(t.taskNo) === 1) ?? tasks.reduce((a, b) => ((a.taskOffset ?? 9999) < (b.taskOffset ?? 9999) ? a : b), tasks[0])
        const forecastBaseMs = task1?.actualStart
            ? truncateToMinuteFloor(new Date(task1.actualStart)).getTime()
            : (forecastStartTime ? forecastStartTime.getTime() : truncateToMinuteFloor(new Date()).getTime())
        const forecastMap = computeForecastTimesMap(tasks, forecastBaseMs)
        const sortedByOffset = [...tasks].sort((a, b) => (a.taskOffset ?? 0) - (b.taskOffset ?? 0))
        const firstTask = sortedByOffset[0]
        const lastTask = sortedByOffset[sortedByOffset.length - 1]
        const assignedDate = firstTask?.plannedStart ? new Date(firstTask.plannedStart) : (runbookForecastStart ?? forecastStartTime ?? new Date())
        const plannedFinishDate = lastTask?.plannedFinish ? new Date(lastTask.plannedFinish) : new Date(forecastBaseMs + 60 * 60 * 1000)
        let forecastFinishMs = forecastBaseMs
        forecastMap.forEach(({ forecastFinishMs: fm }) => {
            if (fm > forecastFinishMs) forecastFinishMs = fm
        })
        const forecastFinishDate = new Date(forecastFinishMs)
        const nowMs = truncateToMinuteFloor(new Date()).getTime()
        const elapsedSeconds = Math.floor((nowMs - assignedDate.getTime()) / 1000)
        const remainingSeconds = Math.floor((forecastFinishMs - nowMs) / 1000)
        const isOverdue = remainingSeconds < 0
        const forecastRunbookDurationSeconds = Math.max(0, (forecastFinishMs - forecastBaseMs) / 1000)
        const rtaStr = formatSecondsToDHM(forecastRunbookDurationSeconds)
        const engineerRtoStr = object?.engineerRTO != null ? formatSecondsToDHM(object.engineerRTO) : rtaStr
        const updateMins = recoveryAssignmentsData?.updateTimeRemainingMinutes ?? null
        let nextUpdateDue = '—'
        if (updateMins != null) {
            const formatted = formatSecondsToDHM(Math.abs(updateMins) * 60, { alwaysShowMinutes: true })
            nextUpdateDue = updateMins > 0 ? t('recoverRequests.nextUpdateIn', { time: formatted }) : updateMins < 0 ? t('recoverRequests.nextUpdateBehind', { time: formatted }) : '—'
        }
        return {
            assigned: assignedDate,
            plannedFinish: plannedFinishDate,
            forecastFinish: forecastFinishDate,
            elapsed: formatSecondsToDHM(elapsedSeconds, { alwaysShowMinutes: true }),
            timeRemaining: (isOverdue ? '-' : '') + formatSecondsToDHM(Math.abs(remainingSeconds), { alwaysShowMinutes: true }),
            isOverdue,
            engineerRto: engineerRtoStr,
            rta: rtaStr,
            nextUpdateDue,
            isNextUpdateOverdue: updateMins != null && updateMins < 0,
        }
    }, [tasks, forecastStartTime, object?.engineerRTO, runbookForecastStart, recoveryAssignmentsData?.updateTimeRemainingMinutes])

    const updates: RecoveryHeaderUpdate[] = useMemo(() => {
        const raw = recoveryAssignmentsData?.assignments ?? []
        if (!Array.isArray(raw)) return []
        return (raw as RecoveryAssignment[]).map((a) => ({
            id: a.id ?? `assignment-${a.recoverRequestsID}-${a.actiondatetime ?? ''}`,
            action: a.action ?? '—',
            dateTime: a.actiondatetime ? new Date(a.actiondatetime) : new Date(0),
            username: a.userName ?? (a.userID != null ? String(a.userID) : ''),
            userId: a.userID ?? null,
            message: a.update ?? '',
        }))
    }, [recoveryAssignmentsData])

    const [addUpdateModalOpen, setAddUpdateModalOpen] = useState(false)
    const handleAddUpdate = useCallback(() => setAddUpdateModalOpen(true), [])
    const handleRecoveryUpdateSaved = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['recovery-assignments', requestID] })
    }, [queryClient, requestID])
    const handleViewChildObjects = useCallback(() => {})

    const showHeader = (showBackButton && onBack) || (title != null && title !== '')

    return (
        <div className="space-y-4" style={{ padding: 'var(--spacing-page)' }}>
            {showHeader && (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {showBackButton && onBack && (
                            <Button variant="ghost" size="sm" onClick={onBack}>
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                {t('common.back')}
                            </Button>
                        )}
                        <h1 className="text-xl font-bold">
                            {title ?? t('recoverRequests.runbookUpdate', 'Runbook Update')}
                        </h1>
                    </div>
                </div>
            )}

            {showRecoverySetTabs ? (
            <Tabs defaultValue="dashboard" className="w-full">
                <TabsList className="grid w-full grid-cols-4 lg:max-w-2xl">
                    <TabsTrigger value="dashboard" className="flex items-center gap-1.5">
                        <LayoutDashboard className="h-4 w-4" />
                        {t('recoverRequests.mobilisation.dashboard', 'Dashboard')}
                    </TabsTrigger>
                    <TabsTrigger value="mobilisation" className="flex items-center gap-1.5">
                        <Radio className="h-4 w-4" />
                        {t('recoverRequests.mobilisation.tabTitle', 'Mobilisation')}
                        <Badge
                            variant={mobilisationCompleted >= mobilisationTotal ? 'default' : 'secondary'}
                            className={
                                mobilisationCompleted >= mobilisationTotal
                                    ? 'bg-green-600 hover:bg-green-600'
                                    : 'bg-amber-600 hover:bg-amber-600 text-white'
                            }
                        >
                            {mobilisationCompleted}/{mobilisationTotal}
                        </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="request-ids" className="flex items-center gap-1.5">
                        <ListOrdered className="h-4 w-4" />
                        {t('recoverRequests.mobilisation.requestIds', 'Request IDs')}
                    </TabsTrigger>
                    <TabsTrigger value="objects" className="flex items-center gap-1.5">
                        <Package className="h-4 w-4" />
                        {t('recoverRequests.mobilisation.objects', 'Objects')}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="dashboard" className="mt-4">
                    {isLoading ? (
                        <div className="flex justify-center py-12 text-muted-foreground">
                            {t('recoverRequests.loadingRunbook')}
                        </div>
                    ) : tasks.length === 0 ? (
                        <div className="rounded-lg border p-8 text-center text-muted-foreground">
                            {t('recoverRequests.noRunbook')}
                        </div>
                    ) : (
                        <>
                            {headerData && (
                                <RecoveryTrackingHeader
                                    objectId={objectID}
                                    description={objectDescription ?? ''}
                                    iconFilename={object?.iconFilename}
                                    vendorName={object?.vendorName ?? 'Object'}
                                    assigned={headerData.assigned}
                                    plannedFinish={headerData.plannedFinish}
                                    forecastFinish={headerData.forecastFinish}
                                    elapsed={headerData.elapsed}
                                    timeRemaining={headerData.timeRemaining}
                                    isOverdue={headerData.isOverdue}
                                    engineerRto={headerData.engineerRto}
                                    rta={headerData.rta}
                                    nextUpdateDue={headerData.nextUpdateDue}
                                    isNextUpdateOverdue={headerData.isNextUpdateOverdue}
                                    requestObjectCount={requestObjectCount}
                                    childObjects={[]}
                                    updates={updates}
                                    onAddUpdate={readOnly ? undefined : handleAddUpdate}
                                    onViewChildObjects={handleViewChildObjects}
                                />
                            )}
                            <AddRecoveryUpdateDialog
                                open={addUpdateModalOpen}
                                onOpenChange={setAddUpdateModalOpen}
                                requestID={requestID}
                                objectId={objectID}
                                objectDescription={objectDescription ?? ''}
                                updates={updates}
                                tasks={tasks}
                                onSaved={handleRecoveryUpdateSaved}
                            />
                            <div
                                className="overflow-x-auto"
                                style={{
                                    marginLeft: 'calc(var(--spacing-page) * -2)',
                                    marginRight: 'calc(var(--spacing-page) * -2)',
                                    paddingLeft: 'calc(var(--spacing-page) * 0.05)',
                                    paddingRight: 'calc(var(--spacing-page) * 0.05)',
                                    width: 'calc(100vw - var(--sidebar-width-expanded) - var(--spacing-page) * 0.1)',
                                    maxWidth: 'none',
                                }}
                            >
                                <RunbookRecoveryTable
                                    tasks={tasks}
                                    forecastStartTime={forecastStartTime}
                                    onSave={handleSave}
                                    requestID={requestID}
                                    onPauseRefetchChange={setPauseRefetch}
                                    readOnly={readOnly}
                                    onTaskIssue={handleTaskIssue}
                                />
                            </div>
                        </>
                    )}
                </TabsContent>

                <TabsContent value="mobilisation" className="mt-4">
                    <MobilisationTab requestID={typeof requestID === 'number' ? requestID : undefined} />
                </TabsContent>

                <TabsContent value="request-ids" className="mt-4">
                    <div className="rounded-lg border p-8 text-center text-muted-foreground">
                        {t('recoverRequests.mobilisation.requestIdsPlaceholder', 'Request IDs — content coming soon.')}
                    </div>
                </TabsContent>

                <TabsContent value="objects" className="mt-4">
                    <div className="rounded-lg border p-8 text-center text-muted-foreground">
                        {t('recoverRequests.mobilisation.objectsPlaceholder', 'Objects — content coming soon.')}
                    </div>
                </TabsContent>
            </Tabs>
            ) : (
                <div className="mt-4">
                    {isLoading ? (
                        <div className="flex justify-center py-12 text-muted-foreground">
                            {t('recoverRequests.loadingRunbook')}
                        </div>
                    ) : tasks.length === 0 ? (
                        <div className="rounded-lg border p-8 text-center text-muted-foreground">
                            {t('recoverRequests.noRunbook')}
                        </div>
                    ) : (
                        <>
                            {headerData && (
                                <RecoveryTrackingHeader
                                    objectId={objectID}
                                    description={objectDescription ?? ''}
                                    iconFilename={object?.iconFilename}
                                    vendorName={object?.vendorName ?? 'Object'}
                                    assigned={headerData.assigned}
                                    plannedFinish={headerData.plannedFinish}
                                    forecastFinish={headerData.forecastFinish}
                                    elapsed={headerData.elapsed}
                                    timeRemaining={headerData.timeRemaining}
                                    isOverdue={headerData.isOverdue}
                                    engineerRto={headerData.engineerRto}
                                    rta={headerData.rta}
                                    nextUpdateDue={headerData.nextUpdateDue}
                                    isNextUpdateOverdue={headerData.isNextUpdateOverdue}
                                    requestObjectCount={requestObjectCount}
                                    childObjects={[]}
                                    updates={updates}
                                    onAddUpdate={readOnly ? undefined : handleAddUpdate}
                                    onViewChildObjects={handleViewChildObjects}
                                />
                            )}
                            <AddRecoveryUpdateDialog
                                open={addUpdateModalOpen}
                                onOpenChange={setAddUpdateModalOpen}
                                requestID={requestID}
                                objectId={objectID}
                                objectDescription={objectDescription ?? ''}
                                updates={updates}
                                tasks={tasks}
                                onSaved={handleRecoveryUpdateSaved}
                            />
                            <div
                                className="overflow-x-auto"
                                style={{
                                    marginLeft: 'calc(var(--spacing-page) * -2)',
                                    marginRight: 'calc(var(--spacing-page) * -2)',
                                    paddingLeft: 'calc(var(--spacing-page) * 0.05)',
                                    paddingRight: 'calc(var(--spacing-page) * 0.05)',
                                    width: 'calc(100vw - var(--sidebar-width-expanded) - var(--spacing-page) * 0.1)',
                                    maxWidth: 'none',
                                }}
                            >
                                <RunbookRecoveryTable
                                    tasks={tasks}
                                    forecastStartTime={forecastStartTime}
                                    onSave={handleSave}
                                    requestID={requestID}
                                    onPauseRefetchChange={setPauseRefetch}
                                    readOnly={readOnly}
                                    onTaskIssue={handleTaskIssue}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    )
}
